import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useTitle } from "../useTitle";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, api, ago, type Bench } from "../api";
import { Board, Loading, Notice, PageHead, Row, Status, type Tone } from "../components/Look";
import { rigState } from "./Now";
import { useIsAdmin } from "../session";
import Modal from "../components/Modal";

/**
 * The screen that does not exist today at all: every rig in one list.
 *
 * Online/offline is derived from the last heartbeat rather than stored. A
 * status column would need something to set it to "offline", and the thing that
 * went offline is exactly the thing that cannot.
 */
export default function Benches() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  useTitle(t("benches.title"));
  const [loaded, setLoaded] = useState(false);
  const [all, setBenches] = useState<Bench[]>([]);
  // The product's Benches tab links here with ?project=: its benches only.
  const [params] = useSearchParams();
  const only = Number(params.get("project")) || 0;
  const benches = only ? all.filter((b) => b.project_id === only) : all;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // The one dialog left on the list; editing, tokens and deleting are on the
  // bench's Setup tab (BenchAdmin).
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setBenches((await api.benches()).benches);
      setLoaded(true);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
    // Poll rather than push: the interesting change is a heartbeat going quiet,
    // and 10s is well inside the 120s the server calls offline.
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      setError("");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <PageHead
        title={t("benches.heading")}
        answer={
          benches.length > 0
            ? `${t("now.rigs", { count: benches.length })}, ${t("now.onlineCount", {
                count: benches.filter((b) => rigState(b).tone === "up").length,
              })}.`
            : undefined
        }
        actions={
          // Creating a bench is an instance administrator's act (POST
          // /api/v1/benches checks _admin), so it is offered to one.
          isAdmin ? (
            <button className="btn-primary" onClick={() => setCreating(true)} disabled={busy}>
              {t("benches.newBench")}
            </button>
          ) : undefined
        }
      />
      {!loaded && <Loading label={t("common.loading")} />}

      {error && <Notice tone="error">{error}</Notice>}


      {loaded && benches.length === 0 && !error && (
        <p className="text-sm text-slate-500">
          {t("benches.empty")}
        </p>
      )}

      <Board>
        {benches.map((b) => {
          const st = rigState(b);
          const tone: Tone = st.tone === "up" ? (b.lease ? "warn" : "good") : "off";
          const facts = [
            `${t("benches.lastSeen")} ${ago(b.last_seen_at)}`,
            `${b.run_count} ${t("benches.runs")}`,
            b.agent_version ? `${t("benches.agent")} ${b.agent_version}` : "",
            b.active_run ? `${t("benches.running")} ${b.active_run}` : "",
          ].filter(Boolean).join(", ");
          return (
            <Row
              key={b.id}
              status={<Status tone={tone} title={st.why}>{st.text}</Status>}
              title={
                <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <Link className="font-semibold hover:underline" to={`/benches/${b.id}`}>{b.name}</Link>
                  {!b.registered && (
                    <span
                      title={t("benches.noTokenTitle")}
                      className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
                    >
                      {t("benches.noTokenBadge")}
                    </span>
                  )}
                  {b.project_name && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {b.project_name}
                    </span>
                  )}
                </span>
              }
              sub={
                <div className="space-y-1">
                  {b.description && <p>{b.description}</p>}
                  {b.lease && (
                    <p className="text-amber-800 dark:text-amber-300">
                      <Trans
                        i18nKey={b.lease.purpose ? "benches.heldByFor" : "benches.heldBy"}
                        values={{ actor: b.lease.actor, purpose: b.lease.purpose }}
                        components={{ b: <b /> }}
                      />
                    </p>
                  )}
                  <p>{facts}</p>
                </div>
              }
              aside={
                <>
                  <Link className="btn-quiet" to={`/benches/${b.id}`}>{t("benches.open")}</Link>
                  {/* ONE BUTTON (step K5). Edit, the token and Delete are on the
                      bench's own Setup tab now: twenty buttons on four rows made
                      the list look like a control panel, and Delete sat as loud
                      as Open. */}
                </>
              }
            />
          );
        })}
      </Board>

      {creating && (
        <BenchDialog
          title={t("benches.newBench")}
          onClose={() => setCreating(false)}
          onSubmit={(name, desc) =>
            act(async () => {
              await api.createBench(name, desc);
              setCreating(false);
            })
          }
        />
      )}

    </section>
  );
}


export function Banner({
  children,
  tone = "error",
}: {
  children: React.ReactNode;
  tone?: "error" | "token";
}) {
  const cls =
    tone === "error"
      ? "border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
      : "border-success-300 bg-success-50 text-success-900 dark:border-success-900 dark:bg-success-950 dark:text-success-200";
  return <div className={`mb-4 rounded-md border px-3 py-2 text-sm ${cls}`}>{children}</div>;
}

function Field(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 dark:border-slate-700 dark:bg-slate-950"
    />
  );
}

export function BenchDialog({
  title,
  initialName = "",
  initialDescription = "",
  onClose,
  onSubmit,
}: {
  title: string;
  initialName?: string;
  initialDescription?: string;
  onClose: () => void;
  onSubmit: (name: string, description: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [desc, setDesc] = useState(initialDescription);
  return (
    <Modal title={title} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) onSubmit(name.trim(), desc);
        }}
        className="space-y-3"
      >
        <label className="block text-sm">
          <span className="mb-1 block text-slate-600 dark:text-slate-400">
            {t("benches.name")}
          </span>
          <Field value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-slate-600 dark:text-slate-400">
            {t("benches.description")}
          </span>
          <Field value={desc} onChange={(e) => setDesc(e.target.value)} />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="px-3 py-1.5 text-sm" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white dark:bg-slate-100 dark:text-slate-900"
          >
            {t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Deleting a bench cascades to every run recorded against it, and there is no
 * undo. So the name has to be typed back - the server insists on it too, and
 * this dialog exists so the requirement is discovered before the request rather
 * than as a 400 afterwards.
 */
export function DeleteDialog({
  bench,
  onClose,
  onConfirm,
}: {
  bench: Bench;
  onClose: () => void;
  onConfirm: (typed: string) => void;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  return (
    <Modal title={t("benches.deleteTitle", { name: bench.name })} onClose={onClose}>
      <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
        {/* The bench NAME is what has to be typed back, so it is interpolated
            into its own element rather than left in the sentence: a translator
            who reflows the words cannot accidentally change what to type. */}
        <Trans
          i18nKey="benches.deleteWarn"
          count={bench.run_count}
          values={{ name: bench.name }}
          components={{ b: <b />, name: <b /> }}
        />
      </p>
      <Field value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
      <div className="mt-3 flex justify-end gap-2">
        <button className="px-3 py-1.5 text-sm" onClick={onClose}>
          {t("common.cancel")}
        </button>
        <button
          className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          disabled={typed !== bench.name}
          onClick={() => onConfirm(typed)}
        >
          {t("common.delete")}
        </button>
      </div>
    </Modal>
  );
}
